/**
 * One place that answers "what roles does this person actually have", and one
 * pure function that turns that answer into a landing screen.
 *
 * WHAT THIS IS NOT: it is not the fix for the 2026-08-23 report ("parent sees
 * NOTHING, coach sees admin dashboard"). I assumed a role-source bug and was
 * wrong. Logging in to UAT as each account and decoding the token showed the
 * claims are correct — coach carries roles:['coach'], parent roles:['parent'].
 * The actual cause was that production runs `main`, which was 182 commits behind
 * `develop` and whose dispatcher is two lines: platform admin -> platform
 * console, EVERYONE ELSE -> club console. That alone produces all three
 * symptoms. Recorded here because the next person to read this file will
 * otherwise re-derive the same wrong theory.
 *
 * WHY IT STILL EXISTS: two reasons, neither of them that story.
 *
 * 1. `dashboardFor()` is pure. The dispatch rule was previously only reachable
 *    through a component that needed `useAuth` mocked to test at all — and the
 *    mock supplied `{ tenant: { roles } }`, so the test asserted an assumption
 *    about where roles live rather than the rule itself. A pure function over a
 *    role list cannot have that flaw.
 *
 * 2. A role can legitimately live in more than one place, and TopBar.tsx:587 is
 *    in-repo evidence that it does: "some accounts carry the role only on the
 *    org membership, not user.roles — without this the avatar role subtitle
 *    renders blank." That knowledge lived in one component. Unioning the sources
 *    here is defence against an account shape the codebase has already met, and
 *    it strictly widens detection, so it cannot misroute someone it used to get
 *    right. It fixes no bug observed today.
 *
 * The two places a role may legitimately appear:
 *   1. `tenant.roles`    — the JWT's app_metadata claim (AuthContext.buildTenant),
 *                          admin-only-writable, and org-scoped by construction:
 *                          org-switcher.ts:162-172 writes the target membership's
 *                          roles wholesale into app_metadata, so the claim always
 *                          describes the org named in the same token.
 *   2. `activeOrg.roles` — the org MEMBERSHIP row, fenced by the same-org check.
 *
 * A THIRD source was here and has been REMOVED as a forgery surface:
 * `user.roles` reads `session.user.user_metadata`, which the subject can rewrite
 * at will via supabase.auth.updateUser({ data: … }). This repo has already closed
 * that exact hole twice and says so in both places — AuthContext.tsx:194-200 (C23,
 * the org claim) and apps/api/src/middleware/auth.ts:161-168 (NEW-001, the server
 * side). Nothing ever writes UserMetadata.roles: it is a type in
 * packages/types/src/tenant.ts with no producer anywhere in the API.
 *
 * So it contributed nothing legitimate and let any signed-in user hand themselves
 * roles:['platform_admin'] and render the Kantera-admin console — /platform/* is
 * a bare ProtectedRoute with no role check (App.tsx, ProtectedRoute.tsx:44). The
 * API refuses the DATA independently, so this was a convincing empty shell rather
 * than a breach. It is still exactly the class of bug C23 was written to end.
 *
 * The TopBar comment that motivated unioning in the first place argues FOR the
 * membership and AGAINST this bag: "some accounts carry the role only on the org
 * membership, NOT user.roles" is evidence user.roles is empty, not that it is
 * useful.
 */

import { actingRoles, isChildLoginRoles, type Role } from '@kantera/types';

export interface RoleSources {
  /** TenantContext.roles — the JWT's app_metadata claim. */
  tenantRoles?: readonly Role[] | undefined;
  /** OrgMembership.roles — the active org's membership row. */
  orgRoles?: readonly Role[] | undefined;

  /**
   * LAW 1 GUARD. The org the verified token names, and the org the membership
   * row describes. `orgRoles` is IGNORED unless these match.
   *
   * Normally they always do: the API computes `isActive` as
   * `row.orgId === tenantContext.orgId` (org-switcher.ts:90, using the JWT's
   * org via getActiveOrgId at :48-52), so the "active" membership is by
   * definition the token's org. The hole is staleness — if the cached org list
   * predates an org switch, `orgs.find(o => o.isActive)` still points at the
   * PREVIOUS org while the refreshed token names the new one. Unioning across
   * that gap would grant a role held in org A while operating in org B.
   *
   * Both omitted -> no cross-org check is possible, so orgRoles is ignored.
   * That fails CLOSED, which is the correct direction for a tenant boundary.
   */
  tenantOrgId?: string | undefined;
  orgRolesOrgId?: string | undefined;
}

/**
 * The UNION of every place a role can live, de-duplicated — as the roles the person ACTS with.
 *
 * A union rather than TopBar's `??` fallback: a person can legitimately be an
 * admin by JWT claim and a coach by org membership, and a fallback would see
 * only the first. Losing the second is how someone ends up on the wrong screen.
 *
 * KAN-115: the membership's roles are HELD roles, so the union goes through actingRoles too (the
 * token's already has): an assistant coach lands on the coach dashboard and gets the coach hat.
 */
export function resolveRoles(sources: RoleSources): Role[] {
  const seen = new Set<Role>();

  // Law 1: a membership's roles count only when that membership is provably the
  // same org the verified token names. Unknown org on either side -> drop it.
  const sameOrg =
    typeof sources.tenantOrgId === 'string' &&
    typeof sources.orgRolesOrgId === 'string' &&
    sources.tenantOrgId.length > 0 &&
    sources.tenantOrgId === sources.orgRolesOrgId;

  const lists: Array<readonly Role[] | undefined> = [
    sources.tenantRoles,
    sameOrg ? sources.orgRoles : undefined,
  ];
  for (const list of lists) {
    // The runtime guard is not redundant with the type: these values cross a
    // network boundary (the org membership) and a JWT claim decoded at runtime.
    // A malformed one must not throw on a login screen.
    if (!list || !Array.isArray(list)) continue;
    // Array.isArray() on a `readonly T[]` narrows to `any[]` in TypeScript, which
    // would silently un-type the loop below. Re-anchor to the declared type.
    const items: readonly Role[] = list;
    for (const role of items) {
      if (typeof role === 'string' && role.trim()) seen.add(role);
    }
  }
  return withoutParentForChildLogin(actingRoles([...seen]));
}

/**
 * KAN-143: a child's own login never counts as holding 'parent'.
 *
 * A child's own login is 'player' and neither an admin nor a coach (isChildLoginRoles, the one
 * rule in @kantera/types the API and the mobile app ask too). Its roles could come to list
 * 'parent' (before KAN-143, accepting a parent invitation sent to the family's shared address
 * merged it in), which opened the parent dashboard, the parent nav and the parent pages. A child's
 * own account is never a parent (the owner's ruling of 2026-09-01: schedule, RSVP, avatar and
 * training only), and the API turns it away from every parent page (403 CHILD_ACCOUNT). Every
 * other role list is returned unchanged.
 */
export function withoutParentForChildLogin(roles: readonly Role[]): Role[] {
  return isChildLoginRoles(roles) ? roles.filter((r) => r !== 'parent') : [...roles];
}

/**
 * The two roles that mean "Kantera staff", as one list.
 *
 * `founder` is a legacy alias for `platform_admin` (packages/types tenant.ts).
 * Anywhere that gates on one must admit the other, so the pair lives here rather
 * than being spelled out at each call site — a gate that lists only one silently
 * locks out half the people it should admit.
 */
export const PLATFORM_ROLES: readonly Role[] = ['founder', 'platform_admin'] as Role[];

export const isPlatformAdminRole = (roles: readonly Role[]): boolean =>
  roles.some((r) => r === 'founder' || r === 'platform_admin');

export const isClubAdminRole = (roles: readonly Role[]): boolean =>
  roles.some((r) => r === 'admin' || r === 'owner');

export const isCoachRole = (roles: readonly Role[]): boolean => roles.some((r) => r === 'coach');

/**
 * KAN-145 (Taylor, 2026-10-09: coaches act only on teams they coach; admins stay club-wide). The team
 * list a page offers for a coach-level action: every team of the club for a club admin or owner,
 * and for anyone else the teams they coach (GET /api/teams?coachedByMe=true), because the API now
 * refuses a coach on any other team. Pass the token's acting roles (tenant.roles).
 */
export const teamsToActOnPath = (roles: readonly Role[], query = ''): string =>
  isClubAdminRole(roles)
    ? `/api/teams${query ? `?${query}` : ''}`
    : `/api/teams?coachedByMe=true${query ? `&${query}` : ''}`;

export const isParentRole = (roles: readonly Role[]): boolean => roles.some((r) => r === 'parent');

/**
 * KAN-221: a child's own login, as the API decides it. The API refuses whole-match footage (match
 * photos included) to a login that holds 'player' and is neither an admin/owner nor a coach
 * (isChildLogin in apps/api/src/services/footage-gate.ts). A parent+player login counts, because
 * the token cannot say which of them is holding it. Keep the two in step.
 *
 * Pass the TOKEN's roles (tenant.roles): the server decides from the token alone, and the hat
 * switcher's view-filtered roles would hide a parent+player login's 'player' in the parent hat.
 * Read through actingRoles, so an assistant coach counts as a coach (KAN-115) whether it is given
 * held or acting roles, and a malformed claim holds nothing.
 */
export function isChildLogin(roles: readonly Role[]): boolean {
  const acting = actingRoles(roles);
  return acting.includes('player') && !isClubAdminRole(acting) && !isCoachRole(acting);
}

/** Which dashboard a set of roles lands on. */
export type DashboardKind = 'platform' | 'coach' | 'parent' | 'club';

/**
 * The landing decision, as a pure function of roles.
 *
 * Pure and exported so it can be tested against ROLE LISTS directly, without
 * mocking an auth context — which is precisely the mock that let the original
 * bug through.
 *
 * Precedence, and the reasoning for each step:
 *   platform admin -> the platform console, outranks everything.
 *   coach, not club staff -> the coach dashboard. Someone who also administers
 *     the club has club work to do and the console is where they do it.
 *   parent, not staff at all -> the parent dashboard.
 *   everyone else -> the club console.
 */
export function dashboardFor(roles: readonly Role[]): DashboardKind {
  if (isPlatformAdminRole(roles)) return 'platform';
  const clubAdmin = isClubAdminRole(roles);
  if (isCoachRole(roles) && !clubAdmin) return 'coach';
  if (isParentRole(roles) && !clubAdmin && !isCoachRole(roles)) return 'parent';
  return 'club';
}

// ---------------------------------------------------------------------------
// View-aware dispatch — the hat the person is actually wearing
// ---------------------------------------------------------------------------

/**
 * The hats a person can wear. Lives here rather than in useProfileView so both
 * the switcher and the dashboard depend on ONE definition (hooks -> utils, no
 * cycle).
 */
export type ProfileViewId = 'platform' | 'staff' | 'coach' | 'parent' | 'referee' | 'player';

/**
 * The dashboard for a DELIBERATELY CHOSEN hat.
 *
 * This is what `dashboardFor` alone could never do. That function ranks roles
 * and shows the winner, so somebody who is a Kantera admin AND a coach AND a
 * parent at the same club only ever sees the highest-ranked one — there was no
 * way to say "right now I am being a coach". Taylor holds all three at AFC
 * Union and hits exactly that.
 *
 * THE ROLE GUARD IS DEFENCE IN DEPTH, and it is genuinely second in line.
 * useProfileView already refuses to make a view active unless it is in
 * availableViews, which is filtered by real roles — so a hat forged in
 * localStorage is normally dropped there and never reaches this function.
 * This re-check exists for every caller that does NOT come through that filter:
 * a direct call, a future consumer, or a change to the filter. It is unit
 * tested directly for that reason — routing a forged hat through the provider
 * proves the FILTER, not this guard, and mistaking one for the other is how a
 * test ends up asserting nothing.
 */
export function dashboardForView(view: ProfileViewId, roles: readonly Role[]): DashboardKind {
  switch (view) {
    case 'platform':
      return isPlatformAdminRole(roles) ? 'platform' : dashboardFor(roles);
    case 'coach':
      return isCoachRole(roles) ? 'coach' : dashboardFor(roles);
    case 'parent':
      return isParentRole(roles) ? 'parent' : dashboardFor(roles);
    case 'staff':
      // Club staff deliberately chose the club console. Role-blind on purpose:
      // 'staff' is also the pre-provider crash-guard default, and that fallback
      // must never depend on roles it may not have loaded yet.
      return 'club';
    default:
      // referee, player — no dashboard of their own exists at '/' yet, so fall
      // back rather than render a blank. Honest, and not a silent failure.
      return dashboardFor(roles);
  }
}

/**
 * Which hat to land on when the person has never chosen one.
 *
 * Deliberately mirrors `dashboardFor`, so a first-ever load is identical to the
 * behaviour before hats were wired in. Choosing a hat is then a change the user
 * makes, never one that happens to them.
 */
export function defaultViewFor(roles: readonly Role[]): ProfileViewId {
  switch (dashboardFor(roles)) {
    case 'platform':
      return 'platform';
    case 'coach':
      return 'coach';
    case 'parent':
      return 'parent';
    default:
      return 'staff';
  }
}
